import { getJaneUserId } from './ragIdentityUtils';

const normalizeRagAnswer = (answer) => (
    answer && typeof answer === 'object' && !Array.isArray(answer)
        ? answer
        : null
);

export const parseRagMessageTimestampMs = (value) => {
    const rawValue = String(value || '').trim();
    if (!rawValue) return null;
    const normalizedValue = rawValue.includes('T') ? rawValue : rawValue.replace(' ', 'T');
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue);
    const parsedValue = Date.parse(hasTimezone ? normalizedValue : `${normalizedValue}Z`);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

export const getRagDisplayTimeZone = (user = null, browserTimeZone = '') => {
    const intlAvailable = typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function';
    if (!intlAvailable) return 'America/Chicago';

    let detectedBrowserTimeZone = String(browserTimeZone || '').trim();
    if (!detectedBrowserTimeZone) {
        try {
            detectedBrowserTimeZone = String(Intl.DateTimeFormat().resolvedOptions().timeZone || '').trim();
        } catch (error) {
            detectedBrowserTimeZone = '';
        }
    }

    const candidates = [user?.timezone, user?.time_zone, detectedBrowserTimeZone, 'America/Chicago'];
    for (const candidate of candidates) {
        const timeZone = String(candidate || '').trim();
        if (!timeZone) continue;
        try {
            new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
            return timeZone;
        } catch (error) {
            // Continue until a valid user/browser timezone is found.
        }
    }
    return 'America/Chicago';
};

const getRagMessageDate = (value) => {
    const timestamp = parseRagMessageTimestampMs(value);
    return timestamp ? new Date(timestamp) : null;
};

export const getRagMessageDateKey = (value, timeZone) => {
    const date = getRagMessageDate(value);
    if (!date) return '';
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: getRagDisplayTimeZone({ timezone: timeZone }),
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return `${values.year || ''}-${values.month || ''}-${values.day || ''}`;
};

export const formatRagMessageDate = (value, timeZone) => {
    const date = getRagMessageDate(value);
    if (!date) return '';
    return new Intl.DateTimeFormat(undefined, {
        timeZone: getRagDisplayTimeZone({ timezone: timeZone }),
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(date);
};

export const formatRagMessageTime = (value, timeZone) => {
    const date = getRagMessageDate(value);
    if (!date) return '';
    return new Intl.DateTimeFormat(undefined, {
        timeZone: getRagDisplayTimeZone({ timezone: timeZone }),
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(date);
};

export const shouldDisplayRagMessageTime = (message = {}, options = {}) => {
    if (String(message?.role || '').trim().toLowerCase() !== 'assistant') return true;

    return !Boolean(
        message?.pending
        || message?.metadata?.streaming
        || options?.assistantResponseActive
    );
};

export const getRagMessageAuthorLabel = (message, currentUser = null, activeChat = null) => {
    if (String(message?.role || '').toLowerCase() === 'assistant') return 'Jane';

    const messageUserId = Number(message?.user_id);
    const currentUserId = getJaneUserId(currentUser);
    const currentUserName = String(currentUser?.name || currentUser?.user || currentUser?.email || '').trim();
    const directAuthor = [
        message?.metadata?.author_snapshot?.name,
        message?.author_name,
        message?.author_user,
        message?.author_email,
        message?.user_name,
        message?.name,
    ].map(value => String(value || '').trim()).find(Boolean);
    if (directAuthor && directAuthor !== 'You') return directAuthor;

    const isCurrentUser = Number.isFinite(messageUserId)
        && currentUserId !== null
        && messageUserId === currentUserId;
    const isLocalUserMessage = String(message?.id || '').startsWith('local-user-');
    if (isCurrentUser || isLocalUserMessage) return currentUserName || 'You';

    if (Number.isFinite(messageUserId) && messageUserId > 0) {
        const sharedUsers = Array.isArray(activeChat?.shared_users) ? activeChat.shared_users : [];
        const matchingSharedUser = sharedUsers.find(sharedUser => Number(sharedUser?.id) === messageUserId);
        if (matchingSharedUser) {
            const sharedUserName = String(matchingSharedUser.name || matchingSharedUser.user || matchingSharedUser.email || '').trim();
            if (sharedUserName) return sharedUserName;
        }

        if (Number(activeChat?.user_id) === messageUserId) {
            const ownerName = String(activeChat?.user_name || activeChat?.owner_name || '').trim();
            if (ownerName) return ownerName;
        }

        return `Member ${Math.trunc(messageUserId)}`;
    }

    return 'Unknown author';
};

export const getRagRerunOpeningText = () => {
    return '';
};

export const isLegacyRagRerunOpeningText = (text = '') => (
    String(text || '').trim() === 'I reran the data for this request using current CaseDB information.'
);

export const isRagRerunMessage = (message = {}) => (
    String(message?.role || '').toLowerCase() === 'assistant'
    && !message.pending
    && !message.metadata?.streaming
    && Number.isInteger(Number(message?.metadata?.rerun_of_message_id))
    && Number(message?.metadata?.rerun_of_message_id) > 0
);

// A paginated save response may omit older/later messages already on screen.
// An inline rerun only replaces its exact saved assistant row, never the thread.
export const mergeRagRerunMessages = (messages = [], persistedMessages = [], assistantMessageId = 0) => {
    const targetId = Number(assistantMessageId);
    if (!Number.isInteger(targetId) || targetId <= 0) return messages;
    const replacement = persistedMessages.find(message => (
        message?.role === 'assistant' && Number(message.id) === targetId
    ));
    if (!replacement) return messages;
    return messages.map(message => (
        message?.role === 'assistant' && Number(message.id) === targetId ? replacement : message
    ));
};

export const findRagRetryResetUserIndex = (messages = [], userMessageIndex = -1, prompt = '') => {
    if (userMessageIndex < 0) return userMessageIndex;

    const normalizedPrompt = String(prompt || '').trim();
    const targetUserId = String(messages[userMessageIndex]?.user_id ?? '').trim();
    if (!normalizedPrompt || !targetUserId) return userMessageIndex;

    let resetIndex = userMessageIndex;
    for (let index = userMessageIndex - 1; index >= 0; index -= 1) {
        const candidate = messages[index];
        // Only collapse the historical duplicate-row bug. A Jane response (or
        // any other row) means the earlier prompt was a separate turn.
        if (!candidate || candidate.role !== 'user') break;

        // Repeated prompts from different members are separate requests. Stop
        // at the first other author so a rerun can never inherit their row.
        if (String(candidate.user_id ?? '').trim() !== targetUserId) break;
        const candidatePrompt = String(candidate.content || candidate.query_text || '').trim();
        if (candidatePrompt !== normalizedPrompt) break;
        resetIndex = index;
    }
    return resetIndex;
};

export const normalizeRagMessages = (messages = []) => messages.map((message) => ({
    id: message.id,
    user_id: message.user_id ?? null,
    role: String(message.role || '').toLowerCase() || 'assistant',
    content: message.content || '',
    answer: normalizeRagAnswer(message.answer),
    sources: message.sources || [],
    files: message.files || [],
    references: message.references || message.metadata?.references || [],
    metadata: message.metadata || null,
    query_text: message.query_text || '',
    author_name: message.author_name || '',
    author_user: message.author_user || '',
    author_email: message.author_email || '',
    user_name: message.user_name || '',
    name: message.name || '',
    created_at: message.created_at
}));

export const bindOptimisticRagRequest = (messages = [], request = {}) => {
    const requestToken = String(request.requestToken || request.request_token || '').trim();
    if (!requestToken || !Array.isArray(messages)) return messages;

    const localUserMessageId = String(request.localUserMessageId || '');
    const localAssistantMessageId = String(request.localAssistantMessageId || '');
    const durableUserMessageId = Number(request.userMessageId || request.user_message_id || 0) || null;
    const durableAssistantMessageId = Number(request.assistantMessageId || request.assistant_message_id || 0) || null;

    return messages.map((message) => {
        const messageId = String(message?.id || '');
        const isOptimisticUser = localUserMessageId !== '' && messageId === localUserMessageId;
        const isOptimisticAssistant = localAssistantMessageId !== '' && messageId === localAssistantMessageId;
        if (!isOptimisticUser && !isOptimisticAssistant) return message;

        return {
            ...message,
            metadata: {
                ...(message.metadata || {}),
                request_token: requestToken,
                durable_request_placeholder: true,
                durable_message_id: isOptimisticUser ? durableUserMessageId : durableAssistantMessageId,
                durable_user_message_id: durableUserMessageId,
                durable_assistant_message_id: durableAssistantMessageId,
            },
        };
    });
};

export const isRagMessageBoundToActiveRequest = (message = {}, activeRequest = {}) => {
    const normalizeIdentity = (value) => {
        const normalized = String(value ?? '').trim();
        return normalized && normalized !== '0' ? normalized : '';
    };
    const activeMessageIds = new Set([
        activeRequest?.user_message_id,
        activeRequest?.assistant_message_id,
    ].map(normalizeIdentity).filter(Boolean));
    const messageIds = [
        message?.id,
        message?.metadata?.durable_message_id,
        message?.metadata?.durable_user_message_id,
        message?.metadata?.durable_assistant_message_id,
    ].map(normalizeIdentity).filter(Boolean);

    if (messageIds.some(messageId => activeMessageIds.has(messageId))) return true;

    const activeRequestToken = String(activeRequest?.request_token || '').trim();
    return activeRequestToken !== ''
        && String(message?.metadata?.request_token || '').trim() === activeRequestToken;
};

export const shouldReconcileStructuredRagAnswerImmediately = (answer = null, intelligence = null) => {
    if (!answer || typeof answer !== 'object' || Array.isArray(answer)) return false;

    const mode = String(answer.mode || intelligence?.retrieval_mode || '').trim().toLowerCase();
    const generatedBy = String(answer.generated_by || '').trim().toLowerCase();
    const taskType = String(answer?.proactive_ai?.task_type || intelligence?.proactive_ai?.task_type || '').trim().toLowerCase();
    const caseId = Number(answer.case_id || intelligence?.case_id || intelligence?.proactive_ai?.case_id || 0);
    const caseAnalysisModes = new Set([
        'case_trial_analysis_queued',
        'case_trial_analysis_complete',
        'case_trial_analysis_failed',
    ]);
    const isCaseAnalysisCard = Number.isFinite(caseId) && caseId > 0 && (
        caseAnalysisModes.has(mode)
        || taskType === 'case_trial_analysis'
        || generatedBy === 'jane_case_trial_analysis'
    );
    const hasMedicalChronology = Array.isArray(answer?.medical_chronology?.events)
        && answer.medical_chronology.events.length > 0;
    const hasSourcedTimeline = Array.isArray(answer?.timeline_result?.timeline)
        && answer.timeline_result.timeline.length > 0;
    const hasReadinessReview = Array.isArray(answer?.readiness_result?.items)
        && answer.readiness_result.items.length > 0;
    const isGeneratedReport = mode === 'generated_report'
        || generatedBy === 'jane_report_generator'
        || Number(answer.report_id || 0) > 0;
    const hasCapabilityRegistry = mode === 'capability_registry_answer'
        && Array.isArray(answer?.capability_registry?.capabilities)
        && answer.capability_registry.capabilities.length > 0;
    const hasCalendarDraftReview = [
        'scheduling_order_calendar_drafts_created',
        'scheduling_order_calendar_pending_confirmation',
    ].includes(mode)
        && Array.isArray(answer.calendar_drafts)
        && answer.calendar_drafts.length > 0;

    return isCaseAnalysisCard
        || hasMedicalChronology
        || hasSourcedTimeline
        || hasReadinessReview
        || isGeneratedReport
        || hasCapabilityRegistry
        || hasCalendarDraftReview;
};

export const getRagMessagePreviewText = (message) => {
    const role = String(message?.role || '').toLowerCase();
    const text = role === 'user'
        ? String(message?.content || message?.query_text || '').trim()
        : String(message?.content || message?.answer?.answer_text || '').trim();
    return text;
};

export const buildRagMobilePreviewMessages = (messages = []) => {
    const previewMessages = [];

    normalizeRagMessages(messages).forEach((message) => {
        const role = String(message.role || '').toLowerCase();
        const assistantQuery = String(message.query_text || '').trim();
        const previous = previewMessages[previewMessages.length - 1];

        if (role === 'assistant' && assistantQuery && previous?.role !== 'user') {
            previewMessages.push({
                id: `${message.id}-query`,
                role: 'user',
                content: assistantQuery,
                query_text: assistantQuery,
                created_at: message.created_at,
            });
        }

        if (getRagMessagePreviewText(message)) {
            previewMessages.push(message);
        }
    });

    return previewMessages.slice(-5);
};

export const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
};
